import React, { useState, useMemo, useEffect } from "react";
import { Edit, Trash2, User2, Send, Plus, Eye } from "lucide-react";
import { Button } from "@shared/ui/button";
import {
  useResendInvite,
  useUpdateUser,
  useTarrifList,
  useUpdateRateMutation,
  useToggleRateMutation,
} from "@features/onboarding/utility-setup/hooks";
import { TariffListItem } from "@features/onboarding/utility-setup/types";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { tarrifFilters } from "./types";
import { PAGE_SIZE } from "@shared/utils/constants";
import { FilterPanel } from "./Filters";
import { Switch } from "@shared/ui/switch";
import { logEvent } from "@shared/analytics/analytics";

const TarrifList: React.FC = () => {
  const navigate = useNavigate();
  const toggleRateMutation = useToggleRateMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  const itemsPerPage = PAGE_SIZE;

  // Parse filters from URL params
  const getFiltersFromUrl = (): tarrifFilters => {
    const page = parseInt(searchParams.get("page") || "1");

    const urlFilters: tarrifFilters = {
      page,
      limit: itemsPerPage,
      remote_utility_id: Number(remoteUtilityId),
    };

      // Parse createdBy
    const createdByParam = searchParams.get("createdBy");
    if (createdByParam) {
      urlFilters.createdBy = createdByParam;
    }

    // Parse utilityService
    const utilityServiceParam = searchParams.get("utilityService");
    if (utilityServiceParam) {
      urlFilters.utilityService = utilityServiceParam;
    }

    // Parse rateType
    const rateTypeParam = searchParams.get("rateType");
    if (rateTypeParam) {
      urlFilters.rateType = rateTypeParam;
    }

    // Parse search query
    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<tarrifFilters>(getFiltersFromUrl);

  // Update filters when URL changes
  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams, remoteUtilityId]);

  // Call API with filters - THIS IS THE KEY FIX!
  const {
    data,
    isLoading: loading,
    error,
  } = useTarrifList(filters, remoteUtilityId);

  const updateUserStatus = useUpdateUser();
  const resendInvite = useResendInvite();

  const handleApplyFilters = (newFilters: Partial<tarrifFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("status");
    params.delete("utilityService");
    params.delete("createdBy");

    params.delete("rateType");
    params.delete("page");

    // ✅ Apply the filters with correct parameter names
    if (newFilters.utilityService) {
      params.set("utilityService", newFilters.utilityService);
    }
    if (newFilters.rateType) {
      params.set("rateType", newFilters.rateType);
    }
      if (newFilters.createdBy) {
      params.set("createdBy", newFilters.createdBy);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("status");
    params.delete("utilityService");
    params.delete("createdBy");
    params.delete("rateType");
    params.delete("page");
    params.delete("query");

    setSearchParams(params);
  };

  // Handle user status toggle
  const handleStatusToggle = (rate: any) => {
    const payload = {
      id: rate.id,
      is_active: !rate.isActive,
    };

    toggleRateMutation.mutate(payload, {
      onSuccess: () => {
        logEvent('Tariff Status Changed', {
          id: rate.id,
          previous_status: rate.isActive,
          new_status: !rate.isActive,
        })
      }
    });
  };

  const onEdit = (row: TariffListItem) => {
    console.log("Edit row:", row);
    navigate("/plan-tarrif/tariff/edit", {
      state: { initialData: row },
    });
  };

  const onView = (row: TariffListItem) => {
    console.log("Edit row:", row);
    navigate(`/plan-tarrif/plan-tarrif-details/${row.id}`, {
      state: { initialData: row },
    });
  };

  // Handle resend invite
  const handleResendInvite = (email: string) => {
    resendInvite.mutate({ email });
  };

  const columns: TableColumn<TariffListItem>[] = [
    {
      key: "rateName",
      header: "Tariff Name",
      width: "w-48",
      render: (_, row) => (
        <span className="text-sm font-medium text-gray-900">
          {row.rateName}
        </span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Type",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "rateTypeDisplay",
      header: "Tarrif Type",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "version",
      header: "Version",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "createdUserRemoteName",
      header: "Created By",
      width: "w-48",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "effectiveDate",
      header: "Valid From",
      width: "w-40",
      render: (value: string) =>
        value
          // ? new Intl.DateTimeFormat("en-GB", {
          //     day: "2-digit",
          //     month: "short",
          //     year: "numeric",
          //   }).format(new Date(value))
          // : "Ongoing",
    },
    {
      key: "expiryDate",
      header: "Valid To",
      width: "w-40",
      render: (value: string) =>
        value
          // ? new Intl.DateTimeFormat("en-GB", {
          //     day: "2-digit",
          //     month: "short",
          //     year: "numeric",
          //   }).format(new Date(value))
          // : "Ongoing",
    },

    {
      key: "isActive",
      header: "Status",
      width: "w-32",
      type: "badge",
      render: (value: boolean) => {
        const label = value ? "ACTIVE" : "INACTIVE";
        const className = value
          ? "bg-green-100 text-green-800 border-green-200 hover:bg-green-200"
          : "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";

        return (
          <span
            className={`text-xs font-medium px-2.5 py-0.5 rounded border ${className}`}
          >
            {label}
          </span>
        );
      },
    },

    {
      key: "actions",
      header: "Action",
      width: "w-36",
      type: "custom",
      render: (_: any, row: TariffListItem) => {
        const isLoading =
          toggleRateMutation.isPending &&
          toggleRateMutation.variables?.id === row.id;

        return (
          <div className="flex items-center gap-3">
            {/* View Tariff */}
            <button
              onClick={() => onView(row)}
              className="text-blue-600 hover:text-blue-900"
              title="View Tariff"
            >
              <Eye className="h-4 w-4" />
            </button>

            {/* Edit Tariff */}
            <button
              onClick={() => onEdit(row)}
              className="text-blue-600 hover:text-blue-900"
              title="Edit Tariff"
            >
              <Edit className="h-4 w-4" />
            </button>

            {/* Toggle Status Switch */}
            <div className="relative">
              <Switch
                checked={row.isActive}
                disabled={isLoading}
                onCheckedChange={() => handleStatusToggle(row)}
                title="Toggle Tariff Status"
                className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300 relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out"
              >
                <span
                  className={`${
                    row.isActive ? "translate-x-5" : "translate-x-0"
                  } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
          </div>
        );
      },
    },
  ];

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  // Handle search - CRITICAL FIX!
  const handleSearch = (searchTerm: string, paramName: string) => {
    console.log(`Search term: "${searchTerm}", Param: "${paramName}"`);

    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      // Set the search param in URL (GenericTable uses 'query' by default)
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    // Reset to page 1 when searching
    // params.set('page', '1');
    setSearchParams(params);
  };

  const currentPage = parseInt(searchParams.get("page") || "1");

  return (
    <div>
      <div className="space-y-4">
        {/* Generic Table */}
        <GenericTable
          data={data?.result || []}
          columns={columns}
        
          searchPlaceholder="Search users..."
          searchParamName="query" // This will be mapped to search_data in API
          searchDebounceMs={300}
          onSearch={handleSearch}
          rowKey="id"
          pagination={{
            currentPage: currentPage,
            totalPages: Math.ceil((data?.count || 0) / itemsPerPage),
            totalItems: data?.count || 0,
            itemsPerPage,
            startItem: (currentPage - 1) * itemsPerPage + 1,
            endItem: Math.min(currentPage * itemsPerPage, data?.count || 0),
          }}
          onPageChange={handlePageChange}
          filterPanel={
            <FilterPanel
              currentFilters={filters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />
          }
          emptyMessage="No users found"
        />
      </div>
    </div>
  );
};

export default TarrifList;
